import "../../../assets/scss/google-cloud-docker-file-list-style.scss";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Button, message, Skeleton } from "antd";
import DockerItem from "../DockerItem";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  getAccessTokenSession,
  getGoogleDriveState,
} from "../../utility/common/session-store";
import {
  clearSelectedDocker,
  updateCurrDockerFolderName,
  updateDockerList,
  updateFolderPath,
} from "../../features/cloud/cloudSlice";
import { RollbackOutlined } from "@ant-design/icons";
import { getDockerFileList } from "../../server/GoogleDrive";
import DockerCreateFolder from "../DockerCreateFolder";
import { DockerBreadcrumb } from "../DockerBreadcrumb";
import LoadingModal from "../LoadingModal";

const baseURL = process.env.REACT_APP_API_BASE_URL;

const DockerFileList = (props: any) => {
  const { t } = useTranslation();
  const { dockerList, goBacktoGoogleList, isLoading, isError } = props;
  const [treatedState, setTreatedState] = useState<any>("");
  const [treatedList, setTreatedList] = useState<any>([]);
  const [messageApi, contextHolder] = message.useMessage();
  const cloudState = useAppSelector((state: any) => state.cloud);
  const userState = useAppSelector((state: any) => state.user);
  const folderPath = useAppSelector((state: any) => state.cloud.folderPath);
  const selectedDocker = useAppSelector(
    (state: any) => state.cloud.selectedDocker,
  );

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const navigationIdRef = useRef(0);
  const [navigating, setNavigating] = useState(false);

  const handleConnect = async () => {
    try {
      const token = getAccessTokenSession();
      const res = await axios.get(baseURL + "auth/drive/connect", {
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        withCredentials: true,
      });
      if (res.data.code === 0 && res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      console.error("Error connecting to Google Drive:", error);
      messageApi.error({
        content: t("Failed to connect to Google Drive."),
      });
    }
  };

  const enterRoot = () => {
    if (navigating) return;
    const currentNavId = ++navigationIdRef.current;
    setNavigating(true);
    dispatch(updateFolderPath(""));
    dispatch(updateCurrDockerFolderName(""));
    getDockerFileList({
      command: 1,
      new_file_name: "",
      search_keyword: "",
      file: "",
      instance_id: selectedDocker.instance_id,
      host_id: selectedDocker.host_id,
      user_id: userState.user_id,
    })
      .then((res: any) => {
        if (currentNavId !== navigationIdRef.current) return;
        dispatch(updateDockerList(res.data.ops_result));
      })
      .catch((err: any) => {
        if (currentNavId !== navigationIdRef.current) return;
        console.error("enterRoot error:", err);
      })
      .finally(() => {
        if (currentNavId !== navigationIdRef.current) return;
        setNavigating(false);
      });
  };

  const jumpToFolder = (path: string) => {
    if (navigating) return;
    const currentNavId = ++navigationIdRef.current;
    setNavigating(true);
    const normalized = path.replace(/\/+$/, "") + "/";

    getDockerFileList({
      command: 2,
      folder: normalized,
      new_file_name: "",
      search_keyword: "",
      file: "",
      instance_id: selectedDocker.instance_id,
      host_id: selectedDocker.host_id,
      user_id: userState.user_id,
    })
      .then((res: any) => {
        if (currentNavId !== navigationIdRef.current) return;
        dispatch(updateDockerList(res.data.ops_result));
        dispatch(updateFolderPath(normalized));
        dispatch(
          updateCurrDockerFolderName(
            normalized.split("/").filter(Boolean).pop() || "",
          ),
        );
      })
      .catch((err: any) => {
        if (currentNavId !== navigationIdRef.current) return;
        console.error("Error", err);
        dispatch(updateCurrDockerFolderName(""));
        dispatch(updateFolderPath(""));
      })
      .finally(() => {
        if (currentNavId !== navigationIdRef.current) return;
        setNavigating(false);
      });
  };

  const parseFileString = (fileString: string) => {
    if (!fileString || fileString.trim() === "") {
      setTreatedList([]);
      return;
    }

    const lines = fileString.split("\n").filter((l) => l.trim() !== "");

    const parsed = lines.map((line) => {
      const parts = line.match(/('[^']*'|"[^"]*"|\S+)/g) || [];

      if (parts.length < 8) return null;

      const permissions = parts[0];
      const size = parseInt(parts[3], 10) || 0;
      const month = parts[4];
      const day = parts[5];
      const time = parts[6];
      let rawName = parts.slice(7).join(" ").trim();

      if (
        (rawName.startsWith("'") && rawName.endsWith("'")) ||
        (rawName.startsWith('"') && rawName.endsWith('"'))
      ) {
        rawName = rawName.slice(1, -1);
      }

      return {
        permissions,
        size,
        date: month + " " + day + " " + time,
        name: rawName,
      };
    });

    const cleanList = parsed.filter(Boolean);
    setTreatedList(cleanList);
  };

  const returnToDrive = () => {
    dispatch(updateFolderPath(""));
    dispatch(updateCurrDockerFolderName(""));
    dispatch(clearSelectedDocker());
    if (goBacktoGoogleList !== undefined) {
      goBacktoGoogleList();
    } else {
      navigate("/cloud");
    }
  };

  const returnToPrevPage = () => {
    if (!folderPath || navigating) {
      return;
    }
    const currentNavId = ++navigationIdRef.current;
    setNavigating(true);

    const currentPath = folderPath.replace(/\/+$/, "");
    const lastSlash = currentPath.lastIndexOf("/");
    const parentPath = lastSlash > 0 ? currentPath.substring(0, lastSlash) : "";
    const segments = parentPath.split("/").filter(Boolean);
    const parentFolderName =
      segments.length > 0 ? segments[segments.length - 1] : "";

    getDockerFileList({
      command: 3,
      new_file_name: "",
      folder: currentPath,
      search_keyword: "",
      file: "",
      instance_id: selectedDocker.instance_id,
      host_id: selectedDocker.host_id,
      user_id: userState.user_id,
    })
      .then((res: any) => {
        if (currentNavId !== navigationIdRef.current) return;
        dispatch(updateDockerList(res.data.ops_result));
        var newPath = "";
        if (parentPath !== "") {
          newPath = parentPath.endsWith("/") ? parentPath : parentPath + "/";
        }
        dispatch(updateFolderPath(newPath));
        dispatch(updateCurrDockerFolderName(parentFolderName));
      })
      .catch((err: any) => {
        if (currentNavId !== navigationIdRef.current) return;
        console.error("returnToPrevPage error:", err);
        dispatch(updateFolderPath(""));
        dispatch(updateCurrDockerFolderName(""));
      })
      .finally(() => {
        if (currentNavId !== navigationIdRef.current) return;
        setNavigating(false);
      });
  };

  useEffect(() => {
    parseFileString(dockerList);
  }, [dockerList]);

  useEffect(() => {
    var newState = getGoogleDriveState();
    setTreatedState(newState);

    if (Object.keys(cloudState.selectedDocker).length == 0) {
      navigate("/instances");
    }
  }, []);

  return (
    <>
      {contextHolder}
      {navigating && <LoadingModal />}
      <div className="docker-file-list-title">
        <div className="title-group">
          <DockerBreadcrumb
            folderPath={folderPath}
            enterRoot={enterRoot}
            jumpToFolder={jumpToFolder}
            disabled={navigating}
          />
          <span>{t("File List Descriptions")}</span>
        </div>
        <div></div>
        <div className="title-button-group">
          <DockerCreateFolder />
          {treatedState == undefined || treatedState === "" ? (
            <Button
              type="primary"
              onClick={handleConnect}
              style={{ marginLeft: ".5em" }}
            >
              {t("Connect to Google Drive")}
            </Button>
          ) : (
            <Button type="primary" onClick={() => returnToDrive()}>
              {t("Go Back to Google Drive")}
            </Button>
          )}
          {folderPath !== "" && (
            <Button type="default" onClick={() => returnToPrevPage()}>
              <RollbackOutlined />
              {t("Return to Previous Layer")}
            </Button>
          )}
        </div>
      </div>
      <div className="docker-file-list">
        <div className="container">
          <div className="header">
            <span className="column">{t("File Name")}</span>
            <span className="column">{t("Date")}</span>
            <span className="column">{t("Size")}</span>
            <span className="column"></span>
          </div>
          {isLoading ? (
            <div style={{ padding: ".5em 1em" }}>
              <Skeleton active />
            </div>
          ) : (
            <>
              {treatedList.map((file: any, index: number) => (
                <DockerItem
                  key={index}
                  dockerList={dockerList}
                  file={file}
                  disabled={navigating}
                />
              ))}
              {treatedList.length === 0 && (
                <div style={{ textAlign: "center", padding: "1em" }}>
                  {isError
                    ? t("An error has occurred, please try again later.")
                    : t("No Files Yet")}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default DockerFileList;
